import { useState } from 'react';
import { useSFTPStatus, useSFTPJobs, usePollNow } from '../hooks/useSFTP';
import type { SFTPJob } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function SFTPMonitorPage() {
  const { data: status } = useSFTPStatus();
  const [page, setPage] = useState(1);
  const { data: jobsData, isLoading } = useSFTPJobs(page);
  const pollNow = usePollNow();

  async function handlePollNow() {
    try {
      await pollNow.mutateAsync();
      toast.success('SFTP poll triggered');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Poll failed');
    }
  }

  const jobs = jobsData?.data ?? [];
  const pagination = jobsData?.pagination;

  // Count by status
  const recentJobs: SFTPJob[] = status?.recentJobs ?? [];
  const completed = recentJobs.filter((j) => j.status === 'completed').length;
  const failed = recentJobs.filter((j) => j.status === 'failed').length;
  const pending = status?.pendingUploads ?? 0;

  return (
    <div className="space-y-6">
      {/* Status Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-amber-100 p-2">
              <ClockIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-silver-500">Pending Uploads</p>
              <p className="text-2xl font-bold text-silver-900">{pending}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-emerald-100 p-2">
              <CheckCircleIcon className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-silver-500">Completed (Recent)</p>
              <p className="text-2xl font-bold text-silver-900">{completed}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-red-100 p-2">
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-silver-500">Failed (Recent)</p>
              <p className="text-2xl font-bold text-silver-900">{failed}</p>
            </div>
          </div>
        </Card>
        <Card>
          <Button onClick={handlePollNow} loading={pollNow.isPending} className="h-full w-full">
            <ArrowPathIcon className="h-5 w-5" /> Poll SFTP Now
          </Button>
        </Card>
      </div>

      {/* Job History */}
      <Card padding={false}>
        <div className="p-6 pb-0">
          <CardHeader title="SFTP Job History" description="All upload and download operations" />
        </div>
        <Table<SFTPJob>
          loading={isLoading}
          data={jobs}
          columns={[
            { key: 'id', header: 'ID' },
            {
              key: 'direction', header: 'Direction',
              render: (r) => r.direction === 'upload'
                ? <span className="flex items-center gap-1 text-blue-600"><ArrowUpTrayIcon className="h-4 w-4" /> Upload</span>
                : <span className="flex items-center gap-1 text-emerald-600"><ArrowDownTrayIcon className="h-4 w-4" /> Download</span>,
            },
            { key: 'job_type', header: 'Type', render: (r) => r.job_type.replace(/_/g, ' ') },
            { key: 'file_name', header: 'File', render: (r) => <span className="font-mono text-xs">{r.file_name}</span> },
            { key: 'status', header: 'Status', render: (r) => <Badge>{r.status}</Badge> },
            { key: 'record_count', header: 'Records', render: (r) => String(r.record_count ?? '-') },
            { key: 'success_count', header: 'Success', render: (r) => String(r.success_count ?? '-') },
            { key: 'failure_count', header: 'Failed', render: (r) => String(r.failure_count ?? '-') },
            {
              key: 'error_message', header: 'Error',
              render: (r) => r.error_message
                ? <span className="text-xs text-red-500">{r.error_message.substring(0, 50)}</span>
                : '-',
            },
            {
              key: 'created_at', header: 'Created',
              render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : '-',
            },
          ]}
        />
        {pagination && (
          <div className="px-4">
            <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
